import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const EventsContext = createContext({
  events: [],
  unread: 0,
  refresh: () => {},
  markAllRead: () => {},
});

const EMPTY = { events: [], unread: 0 };

/**
 * The bell's data (RC-6): the caller's event feed, written by the approval
 * spine when a claim needs them or something happened to theirs.
 *
 * **No polling** — same contract as `PendingApprovalsContext` and for the same
 * reasons: refetch on navigation and on demand, never on a timer. A briefly
 * stale badge is acceptable; the feed itself is refreshed the moment it is
 * opened.
 */
export function EventsProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [feed, setFeed] = useState(EMPTY);

  const refresh = useCallback(async () => {
    if (!user) {
      setFeed(EMPTY);
      return;
    }
    try {
      const { data } = await api.get("/events");
      setFeed({ events: data.events ?? [], unread: data.unread ?? 0 });
    } catch {
      // A bell is not worth an error banner; keep the last known feed rather
      // than flashing an empty one, which reads as "nothing happened".
    }
  }, [user]);

  const markAllRead = useCallback(async () => {
    if (!user || feed.unread === 0) return;
    // Optimistic: the dot disappears as the panel opens; the server catches up.
    setFeed((f) => ({ ...f, unread: 0 }));
    try {
      await api.put("/events/read", {});
    } catch {
      // If the write failed the next refresh restores the true count.
    }
  }, [user, feed.unread]);

  useEffect(() => {
    refresh();
  }, [refresh, location.pathname]);

  return (
    <EventsContext.Provider
      value={{ events: feed.events, unread: feed.unread, refresh, markAllRead }}
    >
      {children}
    </EventsContext.Provider>
  );
}

export const useEvents = () => useContext(EventsContext);
